import { createReducer } from '@reduxjs/toolkit'
import {
  LoadingStatus,
  Filters,
  FilePreviewState,
  Resource,
  FileImportErrorCode,
  InvoiceImport,
  WithIsDialogOpen,
  Invoice,
  InvoiceHistoryItem,
  RejectValue,
} from 'shared/types'
import {
  downloadFile,
  initResource,
  invert,
  setFailed,
  setPending,
  setSucceeded,
} from 'shared/utils'
import * as actions from './actions'
import {
  GetFileImportSummaryResponse,
  AddInvoiceDialogState,
  MultipleDeleteStatus,
  MultipleNotificationStatus,
  MultipleSetAsPaidStatus,
} from './types'

interface State {
  loading: LoadingStatus
  getInvoice: Resource
  deleteInvoice: Resource
  setInvoiceAsPaid: Resource
  importFile: WithIsDialogOpen<Resource>
  filePreview: Resource<FilePreviewState, FileImportErrorCode> & {
    file?: File
  }
  fileImportSummary: WithIsDialogOpen<Resource<GetFileImportSummaryResponse>>
  fileImportSummaryReport: WithIsDialogOpen<Resource>
  fileImportExistingInvoices: WithIsDialogOpen<Resource>
  fileImportLawsuitAlreadyFiled: WithIsDialogOpen<Resource>
  requiredColumns: Resource<string[]>
  addDueSuccessDialog: {
    isOpen: boolean
    number: string
    id: number
  }
  savedMapping: Resource<Record<string, string>>
  addInvoiceDialog: AddInvoiceDialogState
  filters: Filters
  error?: string | null
  refetchInvoices: boolean
  collapsedInvoiceId: number | null
  acceptImport: Resource<Blob | null>
  validateInvoice: Resource
  importTemplate: Resource
  lastInvoiceImports: Resource<InvoiceImport[]>
  preTrialPaymentRequestPreview: Resource
  preTrialRequestForPaymentDialogs: {
    invoiceData: Pick<Invoice, 'contractorName' | 'id' | 'number'> | null
    isGenerateOpen: boolean
    isSuccessOpen: boolean
  }
  batchActions: {
    sendNotifications: Resource<MultipleNotificationStatus | null, RejectValue>
    setAsPaid: Resource<MultipleSetAsPaidStatus | null>
    delete: Resource<MultipleDeleteStatus | null>
  }
  invoiceHistory: Resource<InvoiceHistoryItem[]>
}

const filePreviewInitialData = {
  rowsCount: 0,
  results: {},
  selectedColumns: [],
  fileName: '',
  isDialogOpen: false,
  mappings: {},
  saveMapping: false,
}

export const initialState: State = {
  loading: LoadingStatus.Idle,
  importFile: { ...initResource(), isDialogOpen: false },
  filePreview: initResource(filePreviewInitialData),
  fileImportSummary: {
    ...initResource({
      duplicatedInvoices: [],
      ignoredInvoices: [],
      importSummary: {},
    }),
    isDialogOpen: false,
  },
  fileImportSummaryReport: {
    ...initResource(),
    isDialogOpen: false,
  },
  fileImportExistingInvoices: {
    ...initResource(),
    isDialogOpen: false,
  },
  fileImportLawsuitAlreadyFiled: {
    ...initResource(),
    isDialogOpen: false,
  },
  requiredColumns: initResource([]),
  getInvoice: initResource(),
  deleteInvoice: initResource(),
  setInvoiceAsPaid: initResource(),
  acceptImport: initResource(),
  savedMapping: initResource(),
  validateInvoice: initResource(),
  addDueSuccessDialog: {
    isOpen: false,
    number: '',
    id: 0,
  },
  addInvoiceDialog: {
    isOpen: false,
  },
  filters: {
    invoiceStatuses: [],
    dueDateMin: null,
    dueDateMax: null,
  },
  error: null,
  refetchInvoices: false,
  importTemplate: initResource(),
  preTrialPaymentRequestPreview: initResource(),
  lastInvoiceImports: initResource([]),
  preTrialRequestForPaymentDialogs: {
    invoiceData: null,
    isGenerateOpen: false,
    isSuccessOpen: false,
  },
  batchActions: {
    sendNotifications: initResource(null),
    setAsPaid: initResource(null),
    delete: initResource(null),
  },
  invoiceHistory: initResource([]),
  collapsedInvoiceId: null,
}

export default createReducer(initialState, builder =>
  builder
    .addCase(actions.setFilters, (state, { payload }) => {
      state.filters = payload
    })
    .addCase(actions.patchFilters, (state, { payload }) => {
      state.filters = {
        ...state.filters,
        ...payload,
      }
    })
    .addCase(actions.clearFilters, state => {
      state.filters = {
        invoiceStatuses: [],
        dueDateMin: null,
        dueDateMax: null,
        search: '',
        contractorIds: [],
      }
    })
    .addCase(actions.addInvoice.pending, state => {
      state.loading = LoadingStatus.Pending
    })
    .addCase(
      actions.addInvoice.fulfilled,
      (
        state,
        {
          payload: { id },
          meta: {
            arg: {
              request: { number },
            },
          },
        }
      ) => {
        state.loading = LoadingStatus.Succeeded
        state.addDueSuccessDialog = {
          isOpen: true,
          number,
          id,
        }
      }
    )
    .addCase(actions.addInvoice.rejected, (state, action) => {
      state.error = action.error.message
    })
    .addCase(actions.closeAddDueSuccessDialog, (state, { payload }) => {
      state.filters.search = payload
        ? state.addDueSuccessDialog.number
        : state.filters.search
      state.addDueSuccessDialog = { isOpen: false, number: '', id: 0 }
    })
    .addCase(actions.setInvoicesSearching, (state, { payload }) => {
      state.filters.search = payload
    })
    .addCase(actions.getInvoice.pending, state => {
      setPending(state.getInvoice)
    })
    .addCase(actions.getInvoice.fulfilled, state => {
      setSucceeded(state.getInvoice)
    })
    .addCase(actions.getInvoice.rejected, state => {
      setFailed(state.getInvoice)
    })
    .addCase(actions.deleteInvoice.pending, state => {
      setPending(state.deleteInvoice)
    })
    .addCase(actions.deleteInvoice.fulfilled, state => {
      setSucceeded(state.deleteInvoice)
    })
    .addCase(actions.deleteInvoice.rejected, state => {
      setFailed(state.deleteInvoice)
    })
    .addCase(actions.setInvoiceAsPaid.pending, state => {
      setPending(state.setInvoiceAsPaid)
    })
    .addCase(actions.setInvoiceAsPaid.fulfilled, state => {
      setSucceeded(state.setInvoiceAsPaid)
    })
    .addCase(actions.setInvoiceAsPaid.rejected, state => {
      setFailed(state.setInvoiceAsPaid)
    })
    .addCase(
      actions.getFilePreview.pending,
      (
        state,
        {
          meta: {
            arg: { file },
          },
        }
      ) => {
        setPending(state.filePreview)
        state.filePreview.file = file
        state.filePreview.error = null
        state.filePreview.data.selectedColumns = []
      }
    )
    .addCase(
      actions.getFilePreview.fulfilled,
      (
        state,
        {
          payload: {
            response: { errorCode, ...response },
          },
        }
      ) => {
        setSucceeded(state.filePreview)
        state.filePreview.error = errorCode
        state.filePreview.data.isDialogOpen = true
        state.importFile.isDialogOpen = false
        state.filePreview.data = {
          ...state.filePreview.data,
          ...response,
        }
      }
    )
    .addCase(actions.getFilePreview.rejected, (state, { payload }) => {
      setFailed(state.filePreview, payload?.errorCode)
    })
    .addCase(actions.getInvoicesImportColumns.pending, state => {
      setPending(state.requiredColumns)
    })
    .addCase(
      actions.getInvoicesImportColumns.fulfilled,
      (state, { payload }) => {
        state.requiredColumns.data = payload.response
        setSucceeded(state.requiredColumns)
      }
    )
    .addCase(actions.getInvoicesImportColumns.rejected, (state, action) => {
      setFailed(state.requiredColumns)
    })
    .addCase(actions.setFilePreviewDialogIsOpen, (state, { payload }) => {
      state.filePreview.data.isDialogOpen = payload
    })
    .addCase(actions.setImportDialogState, (state, { payload }) => {
      state.importFile.isDialogOpen = payload
    })
    .addCase(actions.setFilePreviewError, (state, { payload }) => {
      state.filePreview.error = payload
    })
    .addCase(
      actions.patchMapping,
      (state, { payload: { requiredColumn, fileColumn } }) => {
        state.filePreview.data.mappings[fileColumn] = requiredColumn
      }
    )
    .addCase(
      actions.getFileImportSummary.pending,
      (
        state,
        {
          meta: {
            arg: { saveMapping },
          },
        }
      ) => {
        state.filePreview.data.saveMapping = saveMapping
        setPending(state.fileImportSummary)
      }
    )
    .addCase(actions.getFileImportSummary.fulfilled, (state, { payload }) => {
      setSucceeded(state.fileImportSummary, payload.response)
      if (payload.response.duplicatedInvoices.length)
        state.fileImportExistingInvoices.isDialogOpen = true
      else {
        if (payload.response.ignoredInvoices.length)
          state.fileImportLawsuitAlreadyFiled.isDialogOpen = true
        else state.fileImportSummary.isDialogOpen = true
      }
    })
    .addCase(actions.getFileImportSummary.rejected, state => {
      setFailed(state.fileImportSummary)
    })
    .addCase(actions.setAddInvoiceDialogState, (state, { payload }) => {
      state.addInvoiceDialog = payload
    })
    .addCase(actions.setFileImportSummaryDialogState, (state, { payload }) => {
      state.fileImportSummary.isDialogOpen = payload.isDialogOpen
    })
    .addCase(
      actions.setFileImportSummaryReportDialogState,
      (state, { payload }) => {
        state.fileImportSummaryReport.isDialogOpen = payload.isDialogOpen
      }
    )
    .addCase(
      actions.setFileImportExistingInvoicesDialogState,
      (state, { payload: { isDialogOpen } }) => {
        state.fileImportExistingInvoices.isDialogOpen = isDialogOpen
      }
    )
    .addCase(
      actions.setFileImportLawsuitAlreadyFiledDialogState,
      (state, { payload: { isDialogOpen } }) => {
        state.fileImportLawsuitAlreadyFiled.isDialogOpen = isDialogOpen
      }
    )
    .addCase(actions.toggleRefetchInvoices, state => {
      state.refetchInvoices = !state.refetchInvoices
    })
    .addCase(actions.acceptImport.pending, state => {
      setPending(state.acceptImport)
    })
    .addCase(actions.acceptImport.fulfilled, (state, { payload }) => {
      state.filePreview.data.mappings = {}
      setSucceeded(state.acceptImport, payload)
    })
    .addCase(actions.acceptImport.rejected, state => {
      state.filePreview.data.mappings = {}
      setFailed(state.acceptImport)
    })
    .addCase(actions.clearMapping, state => {
      state.filePreview = initResource(filePreviewInitialData)
    })
    .addCase(actions.deleteMapping, (state, { payload: fileColumn }) => {
      if (!state.filePreview.data.mappings[fileColumn]) return
      delete state.filePreview.data.mappings[fileColumn]
    })
    .addCase(actions.getMapping.pending, state => {
      setPending(state.savedMapping)
    })
    .addCase(actions.getMapping.fulfilled, (state, { payload = {} }) => {
      setSucceeded(state.savedMapping, invert(payload))
    })
    .addCase(actions.getMapping.rejected, state => {
      setFailed(state.savedMapping)
    })
    .addCase(actions.setMapping, (state, { payload }) => {
      state.filePreview.data.mappings = payload
    })
    .addCase(actions.validateInvoice.pending, state => {
      setPending(state.validateInvoice)
    })
    .addCase(actions.validateInvoice.fulfilled, state => {
      setSucceeded(state.validateInvoice)
    })
    .addCase(actions.validateInvoice.rejected, (state, { payload }) => {
      setFailed(state.validateInvoice, payload?.errorCode || payload?.error)
    })
    .addCase(actions.getImportTemplate.pending, state => {
      setPending(state.importTemplate)
    })
    .addCase(actions.getImportTemplate.fulfilled, state => {
      setSucceeded(state.importTemplate)
    })
    .addCase(actions.getImportTemplate.rejected, state => {
      setFailed(state.importTemplate)
    })
    .addCase(actions.getLastInvoiceImports.pending, state => {
      setPending(state.lastInvoiceImports)
    })
    .addCase(actions.getLastInvoiceImports.fulfilled, (state, { payload }) => {
      setSucceeded(state.lastInvoiceImports, payload)
    })
    .addCase(actions.getLastInvoiceImports.rejected, state => {
      setFailed(state.lastInvoiceImports)
    })
    .addCase(
      actions.setGeneratePreTrialRequestForPaymentDialogOpen,
      (state, { payload }) => {
        state.preTrialRequestForPaymentDialogs.isGenerateOpen = payload
      }
    )
    .addCase(
      actions.setGeneratePreTrialRequestForPaymentSuccessDialogOpen,
      (state, { payload }) => {
        state.preTrialRequestForPaymentDialogs.isSuccessOpen = payload
      }
    )
    .addCase(actions.setPreTrialRequestInvoiceData, (state, { payload }) => {
      state.preTrialRequestForPaymentDialogs.invoiceData = payload
    })
    .addCase(actions.getPreTrialPaymentRequestPreview.pending, state => {
      setPending(state.preTrialPaymentRequestPreview)
    })
    .addCase(actions.getPreTrialPaymentRequestPreview.fulfilled, state => {
      setSucceeded(state.preTrialPaymentRequestPreview)
    })
    .addCase(actions.getPreTrialPaymentRequestPreview.rejected, state => {
      setFailed(state.preTrialPaymentRequestPreview)
    })
    .addCase(actions.resetBatchActionsState, state => {
      state.batchActions = initialState.batchActions
    })
    .addCase(actions.deleteMultipleInvoices.pending, state => {
      setPending(state.batchActions.delete)
    })
    .addCase(actions.deleteMultipleInvoices.fulfilled, (state, { payload }) => {
      setSucceeded(state.batchActions.delete, payload)
    })
    .addCase(actions.deleteMultipleInvoices.rejected, state => {
      setFailed(state.batchActions.delete)
    })
    .addCase(actions.setMultipleInvoicesAsPaid.pending, state => {
      setPending(state.batchActions.setAsPaid)
    })
    .addCase(
      actions.setMultipleInvoicesAsPaid.fulfilled,
      (state, { payload }) => {
        setSucceeded(state.batchActions.setAsPaid, payload)
      }
    )
    .addCase(actions.setMultipleInvoicesAsPaid.rejected, state => {
      setFailed(state.batchActions.setAsPaid)
    })
    .addCase(actions.sendMultipleNotifications.pending, state => {
      setPending(state.batchActions.sendNotifications)
    })
    .addCase(
      actions.sendMultipleNotifications.fulfilled,
      (state, { payload }) => {
        setSucceeded(state.batchActions.sendNotifications, payload)
      }
    )
    .addCase(
      actions.sendMultipleNotifications.rejected,
      (state, { payload }) => {
        if (payload) setFailed(state.batchActions.sendNotifications, payload[0])
        else setFailed(state.batchActions.sendNotifications)
      }
    )
    .addCase(actions.getInvoiceHistory.pending, state => {
      setPending(state.invoiceHistory)
    })
    .addCase(actions.getInvoiceHistory.fulfilled, (state, { payload }) => {
      setSucceeded(state.invoiceHistory, payload)
    })
    .addCase(actions.getInvoiceHistory.rejected, state => {
      setFailed(state.invoiceHistory)
    })
    .addCase(actions.downloadImportReport, (state, { payload }) => {
      downloadFile(state.acceptImport.data as Blob, payload)
    })
    .addCase(actions.setCollapsedInvoiceId, (state, { payload }) => {
      state.collapsedInvoiceId = payload
    })
)
